import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { getAuthHeaders } from '../lib/authHeaders';
import {
  Trophy, Target, Lock, Unlock, CheckCircle, XCircle,
  ChevronLeft, ChevronRight, Lightbulb, Send, Award,
  Zap, Star, Skull, Crown, Loader2, Swords, Shield,
  Heart, Flame, Snowflake, Volume2, VolumeX, RotateCcw,
  ChevronUp, ChevronDown, Info, Play, X, Eye, EyeOff,
  Gift, Clock, RefreshCw, Pause, Timer, User, Coins,
  CreditCard, ShoppingCart, Wallet, CircleDollarSign,
  Sparkles, BookOpen, HelpCircle
} from 'lucide-react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Progress } from '../components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { cn } from '../lib/utils';
import confetti from 'canvas-confetti';
import SEO, { PageSEO } from '../components/SEO';

import { API } from '../config/api';

// getAuthHeaders imported from shared utility

// Deep sea background image
const BACKGROUND_IMAGE = "https://customer-assets.emergentagent.com/job_a66fc6e1-62a4-4443-9b8f-1d64a3c90aa6/artifacts/f2pbq0wq_intro-1690493409.jpg";

// Villain Images - Map villain names to uploaded images (from database image_url takes priority, this is legacy fallback)
const VILLAIN_IMAGES = {
  'DataDolphin': 'https://customer-assets.emergentagent.com/job_a66fc6e1-62a4-4443-9b8f-1d64a3c90aa6/artifacts/xk57hsmc_ChatGPT%20Image%20Dec%2021%2C%202025%2C%2001_45_18%20AM.png',
  'ByteWhale': 'https://customer-assets.emergentagent.com/job_a66fc6e1-62a4-4443-9b8f-1d64a3c90aa6/artifacts/g231ntde_ChatGPT%20Image%20Dec%2021%2C%202025%2C%2001_43_47%20AM.png',
  'CyberCrab': 'https://customer-assets.emergentagent.com/job_a66fc6e1-62a4-4443-9b8f-1d64a3c90aa6/artifacts/nb4fafot_ChatGPT%20Image%20Dec%2021%2C%202025%2C%2001_42_49%20AM.png',
};

// Default Token costs per tier (can be overridden in DB)
const DEFAULT_TIER_TOKEN_COSTS = {
  1: 5, 2: 10, 3: 25, 4: 50, 5: 100,
};

// Comprehensive "Did You Know" facts for each challenge category
const DID_YOU_KNOW_FACTS = {
  'networking_basics': [
    "💡 The OSI model has 7 layers: Physical, Data Link, Network, Transport, Session, Presentation, and Application.",
    "🌐 A router operates at Layer 3 (Network Layer) and makes decisions based on IP addresses.",
    "🔌 HTTP uses port 80 by default, while HTTPS uses port 443 for secure communication.",
    "📡 The Transport layer (Layer 4) uses TCP for reliable delivery and UDP for faster, unreliable delivery.",
    "🏢 MAC addresses are 48-bit identifiers burned into network interface cards at Layer 2.",
    "⚡ DNS translates human-readable domain names into IP addresses computers can understand.",
  ],
  'linux_basics': [
    "🐧 Linux was created by Linus Torvalds in 1991 as a free, open-source operating system.",
    "📁 The 'ls -la' command shows all files (including hidden) with detailed permissions info.",
    "🔐 The chmod command uses octal numbers: 7=rwx, 6=rw-, 5=r-x, 4=r--, 0=no permissions.",
    "👤 In Linux, everything is a file - including devices, directories, and even processes!",
    "🔧 The /etc directory contains system configuration files for all users and services.",
    "💻 Bash (Bourne Again Shell) is the most common shell in Linux distributions.",
  ],
  'security_basics': [
    "🔒 CIA in cybersecurity stands for Confidentiality, Integrity, and Availability - the security triad.",
    "🎣 Phishing attacks account for over 80% of reported security incidents worldwide.",
    "🦠 The first computer virus, 'Creeper', was created in 1971 and displayed 'I'm the creeper, catch me if you can!'",
    "🛡️ Defense in depth means using multiple layers of security controls throughout an IT system.",
    "🔑 Multi-factor authentication (MFA) requires two or more verification methods to access resources.",
    "⚠️ Social engineering exploits human psychology rather than technical vulnerabilities.",
  ],
  'database_basics': [
    "📊 SQL (Structured Query Language) was developed at IBM in the early 1970s.",
    "🔍 The SELECT statement is the most commonly used SQL command for retrieving data.",
    "🗄️ ACID properties ensure database reliability: Atomicity, Consistency, Isolation, Durability.",
    "📝 NoSQL databases like MongoDB store data in flexible, JSON-like documents instead of tables.",
    "🔗 Foreign keys establish relationships between tables in relational databases.",
    "⚡ Database indexes speed up queries but slow down write operations.",
  ],
  'subnetting': [
    "🔢 A /24 subnet mask (255.255.255.0) provides 254 usable host addresses.",
    "📐 Subnetting divides networks into smaller, more manageable segments for security and efficiency.",
    "🌍 CIDR (Classless Inter-Domain Routing) notation replaced classful networking in 1993.",
    "🧮 To find the number of hosts: 2^(32-prefix) - 2 (subtract network and broadcast addresses).",
    "📍 The subnet mask determines which portion of an IP address identifies the network vs. host.",
    "🔒 Subnetting improves security by isolating network segments from each other.",
  ],
  'firewalls': [
    "🧱 The first commercial firewall was introduced in 1988 as packet filters.",
    "🔍 Stateful firewalls track the state of active connections to make smarter filtering decisions.",
    "🚀 Next-generation firewalls (NGFWs) can inspect application-layer traffic and detect malware.",
    "📋 Firewall rules are processed in order - place specific rules before general ones!",
    "🌐 A DMZ (Demilitarized Zone) is a network segment that sits between internal and external networks.",
    "⛔ Default deny: Block everything by default, then allow only what's necessary.",
  ],
  'encryption': [
    "🔐 AES-256 uses a 256-bit key and is considered military-grade encryption.",
    "🔑 RSA encryption, invented in 1977, uses the mathematical difficulty of factoring large primes.",
    "🔄 Symmetric encryption uses one key for both encryption and decryption (faster but key distribution is hard).",
    "🔏 Asymmetric encryption uses a public/private key pair (slower but solves key distribution).",
    "#️⃣ Hashing is one-way - you can't decrypt a hash back to the original data.",
    "⚛️ Quantum computers may break current RSA encryption, spurring research into post-quantum cryptography.",
  ],
  'active_directory': [
    "🏛️ Active Directory was first introduced with Windows 2000 Server.",
    "📡 LDAP (Lightweight Directory Access Protocol) is used to query Active Directory on port 389.",
    "📋 Group Policy Objects (GPOs) manage settings for users and computers across the domain.",
    "🌳 AD uses a hierarchical structure: Forests > Domains > Organizational Units > Objects.",
    "🎫 Kerberos is the authentication protocol used in Active Directory environments.",
    "👥 AD groups can be Security groups (for permissions) or Distribution groups (for email).",
  ],
  'phishing': [
    "🎣 Spear phishing targets specific individuals with personalized, researched attacks.",
    "🐋 Whaling attacks target high-level executives like CEOs and CFOs.",
    "📧 Check sender email addresses carefully - attackers often use look-alike domains.",
    "🔗 Hover over links before clicking to see the actual destination URL.",
    "⚡ Business Email Compromise (BEC) costs organizations billions annually.",
    "🛡️ Security awareness training reduces successful phishing attacks by up to 70%.",
  ],
  'malware': [
    "💀 Ransomware encrypts files and demands payment - never pay the ransom if possible!",
    "🐛 Worms self-replicate and spread across networks without user interaction.",
    "🐴 Trojans disguise themselves as legitimate software to trick users into installing them.",
    "👁️ Spyware secretly monitors user activity and sends data to attackers.",
    "🧨 Logic bombs are malware that activates when specific conditions are met.",
    "🔄 Polymorphic malware changes its code to avoid detection by antivirus software.",
  ],
  'penetration_testing': [
    "🔍 Reconnaissance is the first phase - gather information about the target without touching it.",
    "🚪 Port scanning identifies open services using tools like Nmap (-sS for SYN scan).",
    "💣 Exploitation attempts to gain unauthorized access using discovered vulnerabilities.",
    "📝 Always get written authorization before conducting any penetration test!",
    "🎯 The goal is to identify vulnerabilities before malicious attackers do.",
    "📊 A pentest report should include findings, risk ratings, and remediation recommendations.",
  ],
  'incident_response': [
    "📋 The IR lifecycle: Preparation → Identification → Containment → Eradication → Recovery → Lessons Learned.",
    "⏱️ Time to detect and contain a breach significantly impacts the total cost.",
    "📸 Document everything during an incident - screenshots, logs, and timelines.",
    "🔒 Containment prevents the incident from spreading while you investigate.",
    "🧹 Eradication removes the threat completely from all affected systems.",
    "📚 Post-incident reviews identify improvements for future incident handling.",
  ],
  'network_security': [
    "🔐 HTTPS uses TLS to encrypt web traffic on port 443.",
    "🔀 A switch segments network traffic at Layer 2 using MAC addresses.",
    "🛡️ VPNs create encrypted tunnels for secure remote access.",
    "📡 IDS (Intrusion Detection) alerts you; IPS (Intrusion Prevention) can block attacks automatically.",
    "🔒 Network segmentation limits lateral movement if an attacker breaches the perimeter.",
    "🌐 Zero Trust assumes no user or device is trusted by default, even inside the network.",
  ],
  'forensics': [
    "🔬 MD5 produces a 128-bit hash, but SHA-256 (256-bit) is more secure for forensics.",
    "📜 Chain of custody documents everyone who handled evidence and when.",
    "💾 Create a forensic image (bit-for-bit copy) before analyzing any evidence.",
    "🕐 Timestamps can be found in file metadata, logs, and browser history.",
    "🔍 Volatility order: Registers > RAM > Disk > Remote logs - collect most volatile first.",
    "⚖️ Forensic evidence must be admissible in court - follow proper procedures!",
  ],
  'cryptography': [
    "🔑 RSA key sizes of 2048-bit or higher are currently considered secure.",
    "✍️ Digital signatures use the sender's private key to prove authenticity and integrity.",
    "🔄 Diffie-Hellman enables secure key exchange over an insecure channel.",
    "📜 X.509 certificates bind public keys to identities and are used in HTTPS.",
    "🎲 Cryptographic random number generators must be truly unpredictable.",
    "⏳ Key rotation limits the damage if a key is compromised.",
  ],
  'reverse_engineering': [
    "🔧 IDA Pro and Ghidra are popular disassemblers for analyzing compiled code.",
    "🐞 Debugging malware requires isolated environments to prevent infection.",
    "📝 Assembly language shows the actual instructions the CPU executes.",
    "🔒 Packers compress and obfuscate executables to evade detection.",
    "🔍 String analysis can reveal hardcoded credentials, URLs, and function names.",
    "⚠️ Only reverse engineer software you own or have permission to analyze!",
  ],
  'web_security': [
    "💉 SQL Injection lets attackers execute arbitrary database queries through user input.",
    "📜 XSS (Cross-Site Scripting) injects malicious scripts into web pages viewed by others.",
    "🖼️ X-Frame-Options header prevents clickjacking by controlling iframe embedding.",
    "🍪 HttpOnly cookies can't be accessed by JavaScript, protecting against XSS theft.",
    "🔐 CSRF tokens verify that requests come from your site, not an attacker's.",
    "📋 OWASP Top 10 lists the most critical web application security risks.",
  ],
  'cloud_security': [
    "☁️ Shared responsibility model: Cloud provider secures infrastructure; you secure your data and apps.",
    "🗝️ IAM (Identity and Access Management) controls who can access what in the cloud.",
    "🔒 Encryption at rest protects stored data; encryption in transit protects data moving over networks.",
    "📊 Cloud Security Posture Management (CSPM) continuously monitors for misconfigurations.",
    "🌍 Data residency laws may require data to stay in specific geographic regions.",
    "📝 Cloud audit logs are essential for security monitoring and compliance.",
  ],
  'siem': [
    "📊 SIEM = Security Information and Event Management - your security command center.",
    "🔔 SIEM correlates events from multiple sources to detect complex attack patterns.",
    "📋 Log aggregation collects logs from servers, firewalls, apps, and endpoints.",
    "⚡ Real-time alerting notifies analysts immediately when suspicious activity occurs.",
    "🔍 Splunk, Elastic Security, and Microsoft Sentinel are popular SIEM solutions.",
    "📈 Baseline normal behavior to better detect anomalies and potential threats.",
  ],
  'zero_trust': [
    "🔒 Zero Trust principle: Never trust, always verify - regardless of network location.",
    "🎯 Micro-segmentation divides networks into small zones for granular access control.",
    "👤 Identity is the new perimeter - verify every user and device before granting access.",
    "📱 Device health checks ensure endpoints meet security requirements before connecting.",
    "🔄 Continuous verification: Don't just authenticate once - verify throughout the session.",
    "📋 Least privilege: Users get only the minimum access needed to do their job.",
  ],
  'threat_hunting': [
    "🔍 MITRE ATT&CK framework maps adversary tactics and techniques for threat hunting.",
    "🎯 Hypothesis-driven hunting starts with an educated guess about attacker behavior.",
    "📊 Indicators of Compromise (IOCs) are forensic artifacts left by attackers.",
    "🕵️ Threat hunters proactively search for threats that evade automated detection.",
    "📝 Document your hunting process and findings to improve future hunts.",
    "🔄 Threat intelligence feeds provide current information about active threats.",
  ],
  'advanced_exploitation': [
    "💥 Buffer overflows overwrite memory to execute malicious code.",
    "🎯 Return-oriented programming (ROP) chains existing code to bypass protections.",
    "🛡️ ASLR (Address Space Layout Randomization) makes memory attacks harder to exploit.",
    "🔒 DEP (Data Execution Prevention) prevents code execution in data memory regions.",
    "📍 Information disclosure vulnerabilities leak memory addresses to bypass ASLR.",
    "🧪 Fuzzing automatically tests software with random inputs to find vulnerabilities.",
  ],
  'red_team': [
    "🔴 Red teams simulate real attackers to test an organization's defenses.",
    "🔀 Lateral movement is how attackers pivot from one system to another inside a network.",
    "🎭 Living off the land uses legitimate tools (PowerShell, WMI) to avoid detection.",
    "🚪 Persistence mechanisms ensure attackers maintain access even after reboots.",
    "💾 Data exfiltration is often the ultimate goal - stealing valuable information.",
    "📋 Rules of engagement define scope, methods, and boundaries for red team operations.",
  ],
  'boss_battle': [
    "🏆 Defense in depth combines multiple security layers for comprehensive protection.",
    "🔗 Cyber Kill Chain: Recon → Weaponization → Delivery → Exploitation → Installation → C2 → Actions.",
    "🎯 APT (Advanced Persistent Threat) actors are sophisticated, state-sponsored attackers.",
    "🛡️ Purple teaming combines red team attacks with blue team defenses for faster improvement.",
    "📊 Security metrics help measure and improve your security posture over time.",
    "🌐 Assume breach: Design your defenses knowing attackers may already be inside.",
  ],
  'default': [
    "🔐 Cybersecurity is everyone's responsibility - not just the IT department!",
    "💰 The average cost of a data breach in 2024 is $4.45 million.",
    "🦠 Over 300,000 new malware samples are created daily worldwide.",
    "📱 Mobile devices are increasingly targeted by attackers - keep them updated!",
    "🔑 Use unique, complex passwords for every account - a password manager helps!",
    "⚡ Patch your systems regularly - many breaches exploit known, unpatched vulnerabilities.",
  ]
};

// Tier colors matching WoW quality colors
const TIER_COLORS = {
  1: { border: 'border-emerald-400', glow: 'shadow-emerald-400/60', text: 'text-emerald-300', bg: 'from-emerald-900/90 to-emerald-950/90', name: 'Common' },
  2: { border: 'border-sky-400', glow: 'shadow-sky-400/60', text: 'text-sky-300', bg: 'from-sky-900/90 to-sky-950/90', name: 'Rare' },
  3: { border: 'border-violet-400', glow: 'shadow-violet-400/60', text: 'text-violet-300', bg: 'from-violet-900/90 to-violet-950/90', name: 'Epic' },
  4: { border: 'border-amber-400', glow: 'shadow-amber-400/60', text: 'text-amber-300', bg: 'from-amber-900/90 to-amber-950/90', name: 'Legendary' },
  5: { border: 'border-rose-400', glow: 'shadow-rose-400/60', text: 'text-rose-300', bg: 'from-rose-900/90 to-rose-950/90', name: 'Mythic' },
};

// Sidebar menu items
const SIDEBAR_MENU = [
  { id: 'special', label: 'Special Offers', icon: '🎁' },
  { id: 'all', label: 'All Villains', icon: '🌊' },
  { id: 'tier1', label: 'Common', icon: '🟢' },
  { id: 'tier2', label: 'Rare', icon: '🔵' },
  { id: 'tier3', label: 'Epic', icon: '🟣' },
  { id: 'tier4', label: 'Legendary', icon: '🟠' },
  { id: 'tier5', label: 'Mythic', icon: '🔴' },
  { id: 'completed', label: 'Completed', icon: '✅' },
  { id: 'locked', label: 'Locked', icon: '🔒' },
];

const SquidTrench = ({ embedded = false }) => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [villains, setVillains] = useState([]);
  const [progress, setProgress] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // Debug log to verify component is mounted
  useEffect(() => {
    console.log('SquidTrench component mounted');
  }, []);
  
  // User squid coins and credits
  const [userCoins, setUserCoins] = useState(100);
  const [userCredits, setUserCredits] = useState(500);
  
  // Pause mode / Update timer
  const [pauseMode, setPauseMode] = useState(false);
  const [updateTimer, setUpdateTimer] = useState({ hours: 0, minutes: 0, seconds: 0 });
  
  // UI state
  const [selectedVillain, setSelectedVillain] = useState(null);
  const [villainDetails, setVillainDetails] = useState(null);
  const [showVillainModal, setShowVillainModal] = useState(false);
  const [showChallengeModal, setShowChallengeModal] = useState(false);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [purchaseType, setPurchaseType] = useState('coins');
  const [activeFilter, setActiveFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 5; // 5 equal square cards
  
  // Auto-rotation state
  const [autoRotateEnabled, setAutoRotateEnabled] = useState(true);
  const autoRotateRef = useRef(null);
  
  // Teaser flip state (reveal a locked villain briefly)
  const [teaserVillainIndex, setTeaserVillainIndex] = useState(null);
  
  // Did You Know rotation
  const [currentFactIndex, setCurrentFactIndex] = useState(0);
  const [currentFacts, setCurrentFacts] = useState([]);
  const factRotationRef = useRef(null);
  
  // Coin insertion state
  const [coinsInserted, setCoinsInserted] = useState(false);
  
  // Current active challenge state
  const [activeChallenge, setActiveChallenge] = useState(null);
  const [challengeStartTime, setChallengeStartTime] = useState(null);
  
  // Challenge state
  const [challenge, setChallenge] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answer, setAnswer] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [questionResults, setQuestionResults] = useState([]);
  const [challengeComplete, setChallengeComplete] = useState(false);
  const [challengeResult, setChallengeResult] = useState(null);

  // Filter villains based on selected filter - computed early for useEffect dependencies
  const filteredVillains = useMemo(() => {
    return villains.filter(villain => {
      const tier = Math.ceil(villain.order / 10);
      switch(activeFilter) {
        case 'special': return villain.order <= 5;
        case 'tier1': return tier === 1;
        case 'tier2': return tier === 2;
        case 'tier3': return tier === 3;
        case 'tier4': return tier === 4;
        case 'tier5': return tier === 5;
        case 'completed': return villain.completed;
        case 'locked': return !villain.unlocked;
        default: return true;
      }
    });
  }, [villains, activeFilter]);

  // Paginate villains - 5 equal square cards - computed early for useEffect dependencies
  const totalPages = Math.ceil(filteredVillains.length / ITEMS_PER_PAGE);
  const paginatedVillains = useMemo(() => {
    return filteredVillains.slice(
      (currentPage - 1) * ITEMS_PER_PAGE,
      currentPage * ITEMS_PER_PAGE
    );
  }, [filteredVillains, currentPage, ITEMS_PER_PAGE]);

  useEffect(() => {
    fetchUser();
    fetchVillains();
    fetchLeaderboard();
    
    return () => {
      if (autoRotateRef.current) clearInterval(autoRotateRef.current);
      if (factRotationRef.current) clearInterval(factRotationRef.current);
    };
  }, []);

  // Auto-rotate carousel
  useEffect(() => {
    if (autoRotateEnabled && villains.length > ITEMS_PER_PAGE) {
      autoRotateRef.current = setInterval(() => {
        setCurrentPage(prev => {
          const maxPages = Math.ceil(filteredVillains.length / ITEMS_PER_PAGE);
          return prev >= maxPages ? 1 : prev + 1;
        });
      }, 8000); // Rotate every 8 seconds
    }
    
    return () => {
      if (autoRotateRef.current) clearInterval(autoRotateRef.current);
    };
  }, [autoRotateEnabled, villains.length, filteredVillains.length]);

  // Teaser flip effect (occasionally reveal a locked villain)
  useEffect(() => {
    const teaserInterval = setInterval(() => {
      if (Math.random() < 0.3) { // 30% chance every 15 seconds
        const lockedVillains = paginatedVillains
          .map((v, i) => ({ ...v, index: i }))
          .filter(v => !v.unlocked);
        
        if (lockedVillains.length > 0) {
          const randomLocked = lockedVillains[Math.floor(Math.random() * lockedVillains.length)];
          setTeaserVillainIndex(randomLocked.index);
          
          // Hide after 2 seconds
          setTimeout(() => setTeaserVillainIndex(null), 2000);
        }
      }
    }, 15000);
    
    return () => clearInterval(teaserInterval);
  }, [paginatedVillains]);

  // Update "Did You Know" facts based on next challenge
  useEffect(() => {
    const nextChallenge = getNextChallenge();
    if (nextChallenge) {
      const category = nextChallenge.challenge_category || 'default';
      const facts = DID_YOU_KNOW_FACTS[category] || DID_YOU_KNOW_FACTS['default'];
      setCurrentFacts(facts);
      setCurrentFactIndex(0);
      
      // Rotate facts every 6 seconds
      if (factRotationRef.current) clearInterval(factRotationRef.current);
      factRotationRef.current = setInterval(() => {
        setCurrentFactIndex(prev => (prev + 1) % facts.length);
      }, 6000);
    }
    
    return () => {
      if (factRotationRef.current) clearInterval(factRotationRef.current);
    };
  }, [villains]);

  const fetchUser = async () => {
    try {
      const res = await axios.get(`${API}/auth/me`, { withCredentials: true, headers: await getAuthHeaders() });
      setUser(res.data);
      setIsAdmin(res.data.role === 'admin');
      setUserCoins(res.data.squid_coins || 100);
      setUserCredits(res.data.credits || 500);
      fetchProgress();
    } catch (error) {
      // Not logged in
    }
  };

  const fetchVillains = async () => {
    try {
      const res = await axios.get(`${API}/mariana/villains`, { 
        withCredentials: true, 
        headers: await getAuthHeaders() 
      });
      setVillains(res.data);
    } catch (error) {
      console.error('Failed to fetch villains:', error);
      toast.error('Failed to load villains');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchProgress = async () => {
    try {
      const res = await axios.get(`${API}/mariana/progress`, { 
        withCredentials: true, 
        headers: await getAuthHeaders() 
      });
      setProgress(res.data);
    } catch (error) {
      // Not logged in or error
    }
  };

  const fetchLeaderboard = async () => {
    try {
      const res = await axios.get(`${API}/mariana/leaderboard`);
      setLeaderboard(res.data);
    } catch (error) {
      console.error('Failed to fetch leaderboard:', error);
    }
  };

  // Get next unlocked villain for the main challenge area
  const getNextChallenge = () => {
    const unlocked = villains.filter(v => v.unlocked && !v.completed);
    return unlocked.length > 0 ? unlocked[0] : null;
  };

  // Get coin cost for a villain
  const getCoinCost = (villain) => {
    if (villain.coin_cost) return villain.coin_cost;
    if (villain.token_cost) return villain.token_cost; // backwards compatibility
    const tier = Math.ceil(villain.order / 10);
    return DEFAULT_TIER_TOKEN_COSTS[tier] || 5;
  };

  const openVillainModal = async (villain) => {
    if (!user) {
      toast.error('Please log in to battle villains');
      navigate('/login');
      return;
    }
    
    if (pauseMode) {
      toast.error('The Trench is currently in maintenance mode. Please wait.');
      return;
    }
    
    if (!villain.unlocked && !isAdmin) {
      toast.error('Defeat the previous villain to unlock this one!');
      return;
    }
    
    setSelectedVillain(villain);
    setTokensInserted(false);
    setShowVillainModal(true);
    
    try {
      const res = await axios.get(`${API}/mariana/villain/${villain.villain_id}`, {
        withCredentials: true,
        headers: await getAuthHeaders()
      });
      setVillainDetails(res.data);
    } catch (error) {
      toast.error('Failed to load villain details');
    }
  };

  const insertCoins = () => {
    const cost = getCoinCost(selectedVillain);
    
    if (userCoins < cost) {
      toast.error(`Not enough Squid Coins! You need ${cost} coins to play.`);
      return;
    }
    
    setUserCoins(prev => prev - cost);
    setCoinsInserted(true);
    toast.success(`${cost} Squid Coins inserted! Ready to battle!`);
  };

  const startChallenge = async () => {
    if (!selectedVillain) return;
    
    if (pauseMode) {
      toast.error('The Trench is currently in maintenance mode. Please wait.');
      return;
    }
    
    if (!coinsInserted && !isAdmin) {
      toast.error('Please insert Squid Coins to play!');
      return;
    }
    
    if (!selectedVillain.unlocked && !isAdmin) {
      toast.error('You must unlock this villain first!');
      return;
    }
    
    try {
      const res = await axios.get(`${API}/mariana/challenge/${selectedVillain.villain_id}`, {
        withCredentials: true,
        headers: await getAuthHeaders()
      });
      setChallenge(res.data);
      setActiveChallenge(selectedVillain);
      setChallengeStartTime(new Date());
      setCurrentQuestionIndex(0);
      setQuestionResults([]);
      setChallengeComplete(false);
      setChallengeResult(null);
      setAnswer('');
      setShowVillainModal(false);
      setShowChallengeModal(true);
    } catch (error) {
      toast.error('Failed to start challenge');
    }
  };

  const submitAnswer = async () => {
    if (!challenge || submitting) return;
    
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/mariana/challenge/submit`, {
        villain_id: selectedVillain.villain_id,
        question_index: currentQuestionIndex,
        answer: answer
      }, {
        withCredentials: true,
        headers: await getAuthHeaders()
      });
      
      const result = res.data;
      
      setQuestionResults(prev => [...prev, {
        index: currentQuestionIndex,
        correct: result.correct,
        points: result.points_earned
      }]);
      
      if (result.correct) {
        toast.success(`Correct! +${result.points_earned} points`);
      } else {
        toast.error(`Incorrect. The answer was: ${result.correct_answer}`);
      }
      
      if (result.all_answered) {
        setChallengeComplete(true);
        setChallengeResult(result);
        
        if (result.victory) {
          triggerVictoryCelebration();
          fetchVillains();
          fetchProgress();
        }
      } else {
        setCurrentQuestionIndex(prev => prev + 1);
        setAnswer('');
      }
    } catch (error) {
      toast.error('Failed to submit answer');
    } finally {
      setSubmitting(false);
    }
  };

  const triggerVictoryCelebration = () => {
    const duration = 3000;
    const animationEnd = Date.now() + duration;
    const randomInRange = (min, max) => Math.random() * (max - min) + min;
    
    const interval = setInterval(() => {
      const timeLeft = animationEnd - Date.now();
      if (timeLeft <= 0) {
        clearInterval(interval);
        return;
      }
      
      confetti({
        particleCount: 50,
        startVelocity: 30,
        spread: 360,
        origin: {
          x: randomInRange(0.2, 0.8),
          y: randomInRange(0.2, 0.6)
        },
        colors: ['#ffd700', '#00ff00', '#00bfff', '#ff00ff', '#ff6600']
      });
    }, 250);
  };

  const closeChallenge = () => {
    setShowChallengeModal(false);
    setChallenge(null);
    setChallengeComplete(false);
    setChallengeResult(null);
    setSelectedVillain(null);
    setVillainDetails(null);
    setActiveChallenge(null);
    setChallengeStartTime(null);
    setTokensInserted(false);
  };

  const getVillainImage = (villain) => {
    // Database image_url takes priority
    if (villain.image_url) return villain.image_url;
    // Fallback to hardcoded images for legacy villains
    if (VILLAIN_IMAGES[villain.name]) return VILLAIN_IMAGES[villain.name];
    return null;
  };

  const formatTimer = (h, m, s) => {
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  };

  const openPurchaseModal = (type) => {
    setPurchaseType(type);
    setShowPurchaseModal(true);
  };

  // Pause auto-rotation on hover
  const handleCarouselHover = (isHovering) => {
    setAutoRotateEnabled(!isHovering);
  };

  const nextChallenge = getNextChallenge();

  if (isLoading) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundImage: `url(${BACKGROUND_IMAGE})`, backgroundSize: 'cover', backgroundPosition: 'center' }}
      >
      <SEO {...PageSEO.worlds} url="https://squidemy.com/worlds" />
      
        <div className="absolute inset-0 bg-black/70" />
        <div className="relative z-10 text-center">
          <Loader2 className="h-16 w-16 animate-spin text-cyan-400 mx-auto mb-4" />
          <p className="text-cyan-300 text-xl font-medium">Descending into the SquidTrench...</p>
        </div>
      </div>
    );
  }

  return (
    <div 
      className={cn("min-h-screen relative", embedded && "pt-0")}
      style={{ backgroundImage: `url(${BACKGROUND_IMAGE})`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundAttachment: 'fixed' }}
    >
      {/* Deep sea gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-slate-950/95 via-blue-950/90 to-slate-950/95" />
      
      <div className="relative z-10">
        {!embedded && <Navigation />}
        
        <main className={cn("max-w-[1600px] mx-auto px-4 pb-4", embedded ? "pt-4" : "pt-32")}>
          
          {/* TOP HEADER BAR - Enhanced deep sea theme */}
          <div className="bg-gradient-to-r from-slate-900/98 via-blue-950/95 to-slate-900/98 backdrop-blur-md rounded-t-2xl border border-cyan-800/40 shadow-xl shadow-cyan-900/20 px-5 py-4">
            <div className="flex items-center justify-between">
              
              {/* Left - User Profile Picture + Title */}
              <div className="flex items-center gap-4">
                <div className="relative">
                  <div className="w-14 h-14 rounded-xl overflow-hidden border-2 border-cyan-500/50 shadow-lg shadow-cyan-500/20">
                    {user?.profile_picture ? (
                      <img src={user.profile_picture} alt={user.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-cyan-600 to-blue-700">
                        <span className="text-2xl">{user?.name?.charAt(0) || '?'}</span>
                      </div>
                    )}
                  </div>
                  {isAdmin && (
                    <div className="absolute -top-1 -right-1 w-5 h-5 bg-rose-500 rounded-full border-2 border-slate-900 flex items-center justify-center shadow-lg">
                      <span className="text-[9px] text-white font-bold">A</span>
                    </div>
                  )}
                </div>
                
                <div>
                  <h1 className="text-2xl font-bold bg-gradient-to-r from-cyan-300 via-blue-300 to-teal-300 bg-clip-text text-transparent" style={{ fontFamily: 'serif' }}>
                    🌊 The SquidTrench
                  </h1>
                  <div className="flex items-center gap-2 text-sm">
                    {user && <span className="text-cyan-200">{user.name}</span>}
                    <span className="text-cyan-400/50">•</span>
                    <span className="text-teal-400">Depth: {progress?.completed || 0}/50</span>
                  </div>
                </div>
              </div>
              
              {/* Center - Stats (REMOVED Credits as requested) */}
              <div className="flex items-center gap-3">
                <button 
                  onClick={() => openPurchaseModal('coins')}
                  className="flex items-center gap-2 bg-gradient-to-r from-amber-900/70 to-yellow-900/70 px-4 py-2 rounded-lg border border-amber-500/50 hover:border-amber-400 hover:from-amber-800/70 hover:to-yellow-800/70 transition-all cursor-pointer group shadow-lg shadow-amber-900/20"
                >
                  <Coins className="h-5 w-5 text-yellow-400 group-hover:scale-110 transition-transform" />
                  <div className="text-left">
                    <span className="text-yellow-400 font-bold block">{userCoins}</span>
                    <span className="text-yellow-400/60 text-[10px]">SQUID COINS</span>
                  </div>
                  <ShoppingCart className="h-3 w-3 text-yellow-400/50 ml-1" />
                </button>
                
                <div className="flex items-center gap-2 bg-slate-900/60 px-4 py-2 rounded border border-amber-900/30">
                  <Star className="h-5 w-5 text-cyan-400" />
                  <div className="text-left">
                    <span className="text-cyan-400 font-bold block">{progress?.total_points || 0}</span>
                    <span className="text-cyan-400/60 text-[10px]">POINTS</span>
                  </div>
                </div>
                
                <div className="flex items-center gap-2 bg-slate-900/60 px-4 py-2 rounded border border-amber-900/30">
                  <Zap className="h-5 w-5 text-purple-400" />
                  <div className="text-left">
                    <span className="text-purple-400 font-bold block">{progress?.total_xp || 0}</span>
                    <span className="text-purple-400/60 text-[10px]">XP</span>
                  </div>
                </div>
                
                <div className="flex items-center gap-2 bg-slate-900/60 px-4 py-2 rounded border border-amber-900/30">
                  <Trophy className="h-5 w-5 text-yellow-500" />
                  <span className="text-yellow-500 font-bold">{progress?.completed || 0}/50</span>
                </div>
              </div>
              
              {/* Right */}
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <span className="text-red-400/60 text-xs block">Hacked</span>
                  <span className="text-red-400 font-bold">{progress?.hacked_count || 0}</span>
                </div>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setShowLeaderboard(true)}
                  className="border-amber-700/50 bg-amber-900/30 text-amber-100 hover:bg-amber-800/50"
                >
                  <Crown className="h-4 w-4 mr-1 text-yellow-500" />
                  Leaderboard
                </Button>
              </div>
            </div>
          </div>

          {/* MAIN LAYOUT */}
          <div className="flex">
            
            {/* LEFT SIDEBAR - Enhanced deep sea theme */}
            <div className="w-52 bg-gradient-to-b from-slate-900/98 via-blue-950/95 to-slate-900/98 border-l border-cyan-800/40 flex-shrink-0 rounded-bl-2xl">
              <div className="p-3 space-y-1">
                {SIDEBAR_MENU.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => { setActiveFilter(item.id); setCurrentPage(1); }}
                    className={cn(
                      "w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left transition-all duration-200 text-sm",
                      activeFilter === item.id
                        ? "bg-gradient-to-r from-cyan-700/50 to-blue-700/50 text-cyan-100 border border-cyan-500/50 shadow-lg shadow-cyan-900/30"
                        : "text-cyan-200/70 hover:bg-blue-900/40 hover:text-cyan-100 border border-transparent"
                    )}
                  >
                    <span>{item.icon}</span>
                    <span className="font-medium text-xs">{item.label}</span>
                  </button>
                ))}
              </div>
              
              {progress && (
                <div className="px-3 pb-3">
                  <div className="p-3 bg-blue-950/50 rounded-xl border border-cyan-800/40">
                    <div className="flex justify-between text-xs mb-2">
                      <span className="text-cyan-300/70">Progress</span>
                      <span className="text-cyan-200">{progress.progress_percentage}%</span>
                    </div>
                    <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-cyan-400 to-teal-400"
                        style={{ width: `${progress.progress_percentage}%` }}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* CENTER CONTENT - Enhanced deep sea theme */}
            <div className="flex-1 bg-gradient-to-br from-slate-900/95 via-blue-950/90 to-slate-900/95 border-x border-cyan-800/40">
              
              {/* Main Challenge Area */}
              <div className="p-4 grid grid-cols-3 gap-4" style={{ minHeight: '280px' }}>
                
                {/* Left - Next Challenge with ROTATING Did You Know */}
                <div className="col-span-2 bg-gradient-to-br from-blue-950/80 to-slate-900/80 rounded-xl border border-cyan-700/40 overflow-hidden shadow-xl shadow-cyan-900/20">
                  {nextChallenge ? (
                    <div className="relative h-full flex flex-col">
                      <div className="absolute inset-0">
                        {getVillainImage(nextChallenge) ? (
                          <img src={getVillainImage(nextChallenge)} alt={nextChallenge.name} className="w-full h-full object-cover opacity-20" />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-cyan-900/30 to-blue-900/30" />
                        )}
                        <div className="absolute inset-0 bg-gradient-to-r from-slate-900/95 via-slate-900/80 to-transparent" />
                      </div>
                      
                      <div className="relative p-5 flex-1 flex flex-col justify-between">
                        <div>
                          <Badge className="bg-cyan-600/80 text-white mb-2 text-xs">Next Challenge</Badge>
                          <h2 className="text-2xl font-bold text-amber-100 mb-1" style={{ fontFamily: 'serif' }}>
                            {nextChallenge.emoji} {nextChallenge.name}
                          </h2>
                          <p className="text-amber-100/60 text-xs mb-3">
                            Tier {Math.ceil(nextChallenge.order / 10)} • {TIER_COLORS[Math.ceil(nextChallenge.order / 10)]?.name}
                          </p>
                        </div>
                        
                        {/* ROTATING Did You Know */}
                        <AnimatePresence mode="wait">
                          <motion.div 
                            key={currentFactIndex}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            transition={{ duration: 0.5 }}
                            className="bg-slate-800/60 rounded-lg p-3 border border-cyan-700/30 mb-3"
                          >
                            <div className="flex items-center gap-2 text-cyan-400 text-xs font-semibold mb-1">
                              <Lightbulb className="h-3 w-3" />
                              DID YOU KNOW?
                              <span className="ml-auto text-cyan-400/50 text-[10px]">
                                {currentFactIndex + 1}/{currentFacts.length}
                              </span>
                            </div>
                            <p className="text-amber-100/80 text-xs leading-relaxed">
                              {currentFacts[currentFactIndex] || "Loading facts..."}
                            </p>
                          </motion.div>
                        </AnimatePresence>
                        
                        <div className="flex items-center gap-3">
                          <Button 
                            onClick={() => openVillainModal(nextChallenge)}
                            disabled={pauseMode}
                            size="sm"
                            className="bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-500 hover:to-orange-500 text-white"
                          >
                            <Swords className="h-4 w-4 mr-1" />
                            View Details
                          </Button>
                          <div className="flex items-center gap-2 text-xs">
                            <Coins className="h-4 w-4 text-yellow-400" />
                            <span className="text-yellow-400 font-bold">
                              {getCoinCost(nextChallenge)} coins to play
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="h-full flex items-center justify-center p-6">
                      <div className="text-center">
                        <Trophy className="h-12 w-12 text-yellow-400 mx-auto mb-3" />
                        <h2 className="text-xl font-bold text-amber-100 mb-1">All Villains Defeated!</h2>
                        <p className="text-amber-100/60 text-sm">You have conquered the SquidTrench!</p>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Right Side Panels */}
                <div className="space-y-3">
                  <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 rounded-xl border border-amber-900/30 p-3">
                    <div className="flex items-center gap-2 text-amber-100/60 text-[10px] mb-1">
                      <Timer className="h-3 w-3" />
                      Active Challenge
                    </div>
                    {activeChallenge ? (
                      <div>
                        <p className="text-amber-100 font-semibold text-sm">{activeChallenge.name}</p>
                        <p className="text-cyan-400 text-xs font-mono">{challengeStartTime?.toLocaleTimeString()}</p>
                      </div>
                    ) : (
                      <p className="text-amber-100/50 text-xs">No active challenge</p>
                    )}
                  </div>
                  
                  <div className={cn(
                    "rounded-xl border p-3",
                    pauseMode ? "bg-gradient-to-br from-red-900/50 to-red-950/50 border-red-700/50" : "bg-gradient-to-br from-slate-800/80 to-slate-900/80 border-amber-900/30"
                  )}>
                    <div className="flex items-center gap-2 text-amber-100/60 text-[10px] mb-1">
                      {pauseMode ? <Pause className="h-3 w-3 text-red-400" /> : <Clock className="h-3 w-3" />}
                      {pauseMode ? 'Maintenance' : 'Next Update'}
                    </div>
                    <p className={cn("text-xl font-mono font-bold", pauseMode ? "text-red-400" : "text-amber-100")}>
                      {formatTimer(updateTimer.hours, updateTimer.minutes, updateTimer.seconds)}
                    </p>
                  </div>
                  
                  <div className="bg-gradient-to-br from-purple-900/30 to-purple-950/30 rounded-xl border border-purple-700/30 p-3">
                    <div className="flex items-center gap-2 text-purple-300/60 text-[10px] mb-1">
                      <Gift className="h-3 w-3" />
                      Coming Soon
                    </div>
                    <p className="text-purple-200 font-semibold text-xs">New offers!</p>
                  </div>
                </div>
              </div>
              
              {/* BOTTOM VILLAIN CAROUSEL - 5 EQUAL SQUARE CARDS */}
              <div 
                className="border-t border-amber-900/30 p-4 bg-slate-900/50"
                onMouseEnter={() => handleCarouselHover(true)}
                onMouseLeave={() => handleCarouselHover(false)}
              >
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-base font-semibold text-amber-100" style={{ fontFamily: 'serif' }}>
                    {SIDEBAR_MENU.find(m => m.id === activeFilter)?.label || 'Villains'}
                    <span className="text-amber-100/50 text-xs ml-2">{filteredVillains.length} available</span>
                  </h3>
                  <div className="flex items-center gap-2">
                    <Button variant="ghost" size="sm" onClick={() => { fetchVillains(); setCurrentPage(1); }} className="text-amber-100/60 hover:text-amber-100 text-xs h-7">
                      <RefreshCw className="h-3 w-3 mr-1" /> Refresh
                    </Button>
                  </div>
                </div>
                
                {/* 5 EQUAL SQUARE VILLAIN CARDS */}
                <div className="grid grid-cols-5 gap-3">
                  {paginatedVillains.map((villain, index) => (
                    <VillainCard 
                      key={villain.villain_id}
                      villain={villain} 
                      onClick={() => openVillainModal(villain)}
                      isAdmin={isAdmin}
                      villainImage={getVillainImage(villain)}
                      pauseMode={pauseMode}
                      coinCost={getCoinCost(villain)}
                      isTeaser={teaserVillainIndex === index}
                    />
                  ))}
                  
                  {/* Fill empty slots */}
                  {Array(ITEMS_PER_PAGE - paginatedVillains.length).fill(0).map((_, i) => (
                    <div key={`empty-${i}`} className="aspect-square rounded-lg bg-slate-800/30 border border-slate-700/30" />
                  ))}
                </div>
                
                {/* Pagination */}
                <div className="flex items-center justify-center gap-4 mt-4 pt-3 border-t border-amber-900/20">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="text-amber-100 hover:bg-amber-800/30 disabled:opacity-30 h-8"
                  >
                    <ChevronLeft className="h-4 w-4" />
                    <ChevronLeft className="h-4 w-4 -ml-2" />
                  </Button>
                  <span className="text-amber-100 text-sm">
                    Page <span className="font-bold">{currentPage}</span>/{totalPages || 1}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages || totalPages === 0}
                    className="text-amber-100 hover:bg-amber-800/30 disabled:opacity-30 h-8"
                  >
                    <ChevronRight className="h-4 w-4" />
                    <ChevronRight className="h-4 w-4 -ml-2" />
                  </Button>
                  
                  {/* Auto-rotate indicator */}
                  <div className="ml-4 flex items-center gap-2 text-xs text-amber-100/50">
                    <div className={cn("w-2 h-2 rounded-full", autoRotateEnabled ? "bg-green-500 animate-pulse" : "bg-slate-600")} />
                    {autoRotateEnabled ? "Auto-rotating" : "Paused"}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>

        {!embedded && <Footer />}
      </div>

      {/* VILLAIN DETAILS MODAL - WIDER */}
      <Dialog open={showVillainModal} onOpenChange={setShowVillainModal}>
        <DialogContent className="max-w-2xl bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 border-2 border-amber-700/50 text-white p-0 overflow-hidden">
          {villainDetails ? (
            <div className="flex">
              <div className="w-1/2 relative">
                {getVillainImage(selectedVillain) ? (
                  <img src={getVillainImage(selectedVillain)} alt={villainDetails.name} className="w-full h-full object-cover min-h-[400px]" />
                ) : (
                  <div className="w-full h-full min-h-[400px] bg-gradient-to-br from-slate-700 to-slate-800 flex items-center justify-center">
                    <span className="text-9xl">{selectedVillain?.emoji}</span>
                  </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent to-slate-900/50" />
                <div className={cn(
                  "absolute top-4 left-4 px-3 py-1 rounded-full text-sm font-bold border",
                  TIER_COLORS[villainDetails.tier]?.border,
                  TIER_COLORS[villainDetails.tier]?.text,
                  "bg-slate-900/80"
                )}>
                  {TIER_COLORS[villainDetails.tier]?.name}
                </div>
              </div>
              
              <div className="w-1/2 p-5 flex flex-col">
                <div className="mb-4">
                  <h2 className={cn("text-2xl font-bold mb-1", TIER_COLORS[villainDetails.tier]?.text)} style={{ fontFamily: 'serif' }}>
                    {villainDetails.name}
                  </h2>
                  <p className="text-amber-100/60 text-sm">{villainDetails.category}</p>
                </div>
                
                <div className="bg-slate-800/50 rounded-lg p-3 border border-amber-900/30 mb-4 flex-1">
                  <p className="text-amber-100/80 text-xs italic leading-relaxed">"{villainDetails.backstory}"</p>
                </div>
                
                <div className="grid grid-cols-2 gap-2 mb-4">
                  <div className="bg-green-900/30 rounded p-2 border border-green-700/30">
                    <div className="flex items-center gap-1 text-green-400 text-[10px] font-semibold mb-1">
                      <Shield className="h-3 w-3" /> STRENGTHS
                    </div>
                    {villainDetails.strengths?.slice(0, 2).map((s, i) => (
                      <p key={i} className="text-green-300/80 text-[10px]">• {s}</p>
                    ))}
                  </div>
                  <div className="bg-red-900/30 rounded p-2 border border-red-700/30">
                    <div className="flex items-center gap-1 text-red-400 text-[10px] font-semibold mb-1">
                      <Target className="h-3 w-3" /> WEAKNESSES
                    </div>
                    {villainDetails.weaknesses?.slice(0, 2).map((w, i) => (
                      <p key={i} className="text-red-300/80 text-[10px]">• {w}</p>
                    ))}
                  </div>
                </div>
                
                <div className="grid grid-cols-4 gap-2 py-3 border-y border-amber-900/30 mb-4">
                  <div className="text-center">
                    <Star className="h-4 w-4 text-cyan-400 mx-auto" />
                    <p className="text-cyan-400 font-bold text-sm">{villainDetails.points}</p>
                    <p className="text-[9px] text-amber-100/50">Points</p>
                  </div>
                  <div className="text-center">
                    <Zap className="h-4 w-4 text-purple-400 mx-auto" />
                    <p className="text-purple-400 font-bold text-sm">{villainDetails.xp}</p>
                    <p className="text-[9px] text-amber-100/50">XP</p>
                  </div>
                  <div className="text-center">
                    <Coins className="h-4 w-4 text-yellow-400 mx-auto" />
                    <p className="text-yellow-400 font-bold text-sm">{getCoinCost(selectedVillain)}</p>
                    <p className="text-[9px] text-amber-100/50">Cost</p>
                  </div>
                  <div className="text-center">
                    <CircleDollarSign className="h-4 w-4 text-emerald-400 mx-auto" />
                    <p className="text-emerald-400 font-bold text-sm">{villainDetails.tier * 10}</p>
                    <p className="text-[9px] text-amber-100/50">Credits</p>
                  </div>
                </div>
                
                {!coinsInserted && !isAdmin ? (
                  <div className="bg-yellow-900/30 border border-yellow-700/50 rounded-lg p-3 mb-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-yellow-400 font-semibold text-sm">Insert Squid Coins to Play</p>
                        <p className="text-yellow-400/60 text-xs">Cost: {getCoinCost(selectedVillain)} coins • You have: {userCoins}</p>
                      </div>
                      <Button 
                        onClick={insertCoins}
                        disabled={userCoins < getCoinCost(selectedVillain)}
                        size="sm"
                        className="bg-gradient-to-r from-yellow-600 to-amber-600 hover:from-yellow-500 hover:to-amber-500"
                      >
                        <Coins className="h-4 w-4 mr-1" />
                        Insert
                      </Button>
                    </div>
                    <p className="text-yellow-400/40 text-[10px] mt-2">⚠️ Squid Coins are non-refundable</p>
                  </div>
                ) : coinsInserted ? (
                  <div className="bg-green-900/30 border border-green-700/50 rounded-lg p-3 mb-3 flex items-center gap-2">
                    <CheckCircle className="h-5 w-5 text-green-400" />
                    <div>
                      <p className="text-green-400 font-semibold text-sm">Coins Inserted!</p>
                      <p className="text-green-400/60 text-xs">Ready to battle</p>
                    </div>
                  </div>
                ) : null}
                
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setShowVillainModal(false)} className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-700" size="sm">
                    Close
                  </Button>
                  <Button 
                    onClick={startChallenge}
                    disabled={(!coinsInserted && !isAdmin) || pauseMode || (!selectedVillain?.unlocked && !isAdmin)}
                    className={cn(
                      "flex-1",
                      (coinsInserted || isAdmin) && !pauseMode && (selectedVillain?.unlocked || isAdmin)
                        ? "bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-500 hover:to-orange-500 text-white"
                        : "bg-slate-700 text-slate-400 cursor-not-allowed"
                    )}
                    size="sm"
                  >
                    <Swords className="h-4 w-4 mr-1" />
                    {!coinsInserted && !isAdmin ? 'Insert Coins' : 'Battle!'}
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-8 w-8 animate-spin text-amber-400" />
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* CHALLENGE MODAL */}
      <Dialog open={showChallengeModal} onOpenChange={closeChallenge}>
        <DialogContent className="max-w-2xl bg-gradient-to-b from-slate-900 to-slate-800 border-2 border-cyan-700/50 text-white">
          {challenge && !challengeComplete ? (
            <>
              <DialogHeader>
                <DialogTitle className="text-xl text-cyan-300 flex items-center gap-2">
                  <Target className="h-5 w-5" />
                  {challenge.challenge_name}
                </DialogTitle>
                <DialogDescription className="text-cyan-400/70">
                  Question {currentQuestionIndex + 1} of {challenge.total_questions}
                </DialogDescription>
              </DialogHeader>
              
              <div className="flex gap-1 my-4">
                {challenge.questions.map((_, i) => (
                  <div key={i} className={cn(
                    "h-2 flex-1 rounded-full transition-colors",
                    i < questionResults.length ? questionResults[i].correct ? "bg-green-500" : "bg-red-500" : i === currentQuestionIndex ? "bg-cyan-500" : "bg-slate-700"
                  )} />
                ))}
              </div>
              
              <div className="bg-slate-800/50 rounded-lg p-6 my-4 border border-cyan-900/30">
                <p className="text-lg text-white mb-4">{challenge.questions[currentQuestionIndex]?.question}</p>
                <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/50">
                  {challenge.questions[currentQuestionIndex]?.points} points
                </Badge>
              </div>
              
              {challenge.questions[currentQuestionIndex]?.type === 'multiple_choice' ? (
                <div className="grid grid-cols-2 gap-3">
                  {challenge.questions[currentQuestionIndex]?.options?.map((option, i) => (
                    <Button
                      key={i}
                      variant={answer === option ? "default" : "outline"}
                      onClick={() => setAnswer(option)}
                      className={cn("py-6 text-left justify-start", answer === option ? "bg-cyan-600 border-cyan-500" : "border-slate-600 hover:border-cyan-500")}
                    >
                      {option}
                    </Button>
                  ))}
                </div>
              ) : (
                <Input
                  value={answer}
                  onChange={(e) => setAnswer(e.target.value)}
                  placeholder="Type your answer..."
                  className="bg-slate-800 border-slate-600 text-white"
                  onKeyDown={(e) => e.key === 'Enter' && submitAnswer()}
                />
              )}
              
              <DialogFooter className="mt-6">
                <Button variant="outline" onClick={closeChallenge} className="border-gray-500 text-gray-300">Forfeit</Button>
                <Button onClick={submitAnswer} disabled={!answer || submitting} className="bg-gradient-to-r from-cyan-500 to-blue-600">
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                  Submit
                </Button>
              </DialogFooter>
            </>
          ) : challengeComplete && challengeResult ? (
            <div className="text-center py-8">
              {challengeResult.victory ? (
                <>
                  <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", duration: 0.5 }}>
                    <Trophy className="h-20 w-20 text-yellow-400 mx-auto mb-4" />
                  </motion.div>
                  <h2 className="text-3xl font-bold text-yellow-400 mb-2" style={{ fontFamily: 'serif' }}>VICTORY!</h2>
                  <p className="text-cyan-300 mb-6">You defeated {selectedVillain?.name}!</p>
                </>
              ) : (
                <>
                  <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", duration: 0.5 }}>
                    <Skull className="h-20 w-20 text-red-400 mx-auto mb-4" />
                  </motion.div>
                  <h2 className="text-3xl font-bold text-red-400 mb-2" style={{ fontFamily: 'serif' }}>DEFEATED!</h2>
                  <p className="text-red-300 mb-4">{selectedVillain?.name} has hacked you!</p>
                  <p className="text-red-400/60 text-sm mb-6">Insert more Squid Coins to try again</p>
                </>
              )}
              
              <div className="bg-slate-800/50 rounded-lg p-6 mb-6 border border-amber-900/30">
                <div className="grid grid-cols-3 gap-4">
                  <div><p className="text-2xl font-bold text-green-400">{challengeResult.total_correct}</p><p className="text-sm text-gray-400">Correct</p></div>
                  <div><p className="text-2xl font-bold text-cyan-400">{challengeResult.total_points}</p><p className="text-sm text-gray-400">Points</p></div>
                  <div><p className="text-2xl font-bold text-purple-400">{Math.round((challengeResult.total_correct / challenge.total_questions) * 100)}%</p><p className="text-sm text-gray-400">Accuracy</p></div>
                </div>
              </div>
              
              {challengeResult.victory && challengeResult.next_villain && (
                <p className="text-cyan-400 mb-4"><Unlock className="h-4 w-4 inline mr-2" />Unlocked: {challengeResult.next_villain.emoji} {challengeResult.next_villain.name}</p>
              )}
              
              <Button onClick={closeChallenge} className="bg-gradient-to-r from-amber-600 to-orange-600">Continue</Button>
            </div>
          ) : (
            <div className="flex items-center justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-cyan-500" /></div>
          )}
        </DialogContent>
      </Dialog>

      {/* PURCHASE MODAL */}
      <Dialog open={showPurchaseModal} onOpenChange={setShowPurchaseModal}>
        <DialogContent className="max-w-md bg-gradient-to-b from-slate-900 to-slate-800 border-2 border-amber-700/50 text-white">
          <DialogHeader>
            <DialogTitle className="text-xl text-amber-100 flex items-center gap-2">
              {purchaseType === 'coins' ? <><Coins className="h-5 w-5 text-yellow-400" /> Purchase Squid Coins</> : <><CircleDollarSign className="h-5 w-5 text-emerald-400" /> Purchase Credits</>}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-3 py-4">
            {purchaseType === 'coins' ? (
              <>
                <p className="text-amber-100/70 text-sm">Squid Coins are used to play challenges across the platform.</p>
                <div className="grid grid-cols-2 gap-3">
                  {[{ amt: 50, price: '$4.99' }, { amt: 120, price: '$9.99' }, { amt: 300, price: '$19.99', best: true }, { amt: 700, price: '$39.99' }].map(pkg => (
                    <button key={pkg.amt} className="p-4 bg-slate-800/50 rounded-lg border border-amber-900/30 hover:border-yellow-500 transition-colors relative">
                      {pkg.best && <Badge className="absolute -top-2 -right-2 bg-red-500 text-white text-[10px]">BEST VALUE</Badge>}
                      <p className="text-yellow-400 font-bold text-xl">{pkg.amt}</p>
                      <p className="text-amber-100/60 text-xs">Tokens</p>
                      <p className="text-emerald-400 font-semibold mt-2">{pkg.price}</p>
                    </button>
                  ))}
                </div>
              </>
            ) : (
              <>
                <p className="text-amber-100/70 text-sm">Credits for special items and power-ups.</p>
                <div className="grid grid-cols-2 gap-3">
                  {[{ amt: 500, price: '$4.99' }, { amt: 1200, price: '$9.99' }].map(pkg => (
                    <button key={pkg.amt} className="p-4 bg-slate-800/50 rounded-lg border border-amber-900/30 hover:border-emerald-500 transition-colors">
                      <p className="text-emerald-400 font-bold text-xl">{pkg.amt}</p>
                      <p className="text-amber-100/60 text-xs">Credits</p>
                      <p className="text-yellow-400 font-semibold mt-2">{pkg.price}</p>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPurchaseModal(false)} className="border-slate-600 text-slate-300">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* LEADERBOARD MODAL */}
      <Dialog open={showLeaderboard} onOpenChange={setShowLeaderboard}>
        <DialogContent className="max-w-lg bg-gradient-to-b from-slate-900 to-slate-800 border-2 border-amber-700/50 text-white">
          <DialogHeader>
            <DialogTitle className="text-xl text-amber-100 flex items-center gap-2" style={{ fontFamily: 'serif' }}>
              <Crown className="h-5 w-5 text-yellow-500" /> Trench Champions
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {leaderboard.map((entry, index) => (
              <div key={entry.user_id} className={cn(
                "flex items-center gap-4 p-3 rounded-lg border",
                index === 0 ? "bg-yellow-500/10 border-yellow-500/50" : index === 1 ? "bg-gray-400/10 border-gray-400/50" : index === 2 ? "bg-orange-500/10 border-orange-500/50" : "bg-slate-800/50 border-slate-700"
              )}>
                <div className={cn("w-8 h-8 rounded-full flex items-center justify-center font-bold", index === 0 ? "bg-yellow-500 text-black" : index === 1 ? "bg-gray-400 text-black" : index === 2 ? "bg-orange-500 text-black" : "bg-slate-700 text-white")}>
                  {entry.rank}
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-white">{entry.name}</p>
                  <p className="text-sm text-cyan-400">{entry.completed}/50</p>
                </div>
                <div className="text-right">
                  <p className="text-yellow-400 font-bold">{entry.total_points}</p>
                  <p className="text-xs text-gray-400">pts</p>
                </div>
              </div>
            ))}
            {leaderboard.length === 0 && <p className="text-center text-gray-400 py-8">No champions yet!</p>}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// VILLAIN CARD COMPONENT - EQUAL SQUARE
const VillainCard = ({ villain, onClick, isAdmin, villainImage, pauseMode, coinCost, isTeaser }) => {
  const tier = Math.ceil(villain.order / 10);
  const tierStyle = TIER_COLORS[tier];
  const isAccessible = villain.unlocked || isAdmin;
  const showContent = isAccessible || isTeaser;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      onClick={onClick}
      className={cn(
        "cursor-pointer transition-all duration-300",
        isAccessible && !pauseMode ? "hover:scale-[1.03] hover:-translate-y-1" : "cursor-not-allowed"
      )}
    >
      <div className={cn(
        "relative rounded-lg overflow-hidden border-2 transition-all duration-300",
        villain.completed ? "border-green-500 shadow-lg shadow-green-500/30" :
        showContent ? `${tierStyle.border} hover:shadow-lg hover:${tierStyle.glow}` :
        "border-slate-700 opacity-70"
      )}>
        {/* SQUARE ASPECT */}
        <div className="aspect-square relative overflow-hidden bg-gradient-to-br from-slate-800 to-slate-900">
          {showContent ? (
            villainImage ? (
              <motion.img 
                src={villainImage} 
                alt={villain.name}
                className="w-full h-full object-contain bg-slate-900"
                initial={isTeaser ? { rotateY: 180 } : {}}
                animate={isTeaser ? { rotateY: 0 } : {}}
                transition={{ duration: 0.6 }}
              />
            ) : (
              <div className={cn("w-full h-full flex items-center justify-center bg-gradient-to-br", tierStyle.bg)}>
                <span className="text-6xl">{villain.emoji}</span>
              </div>
            )
          ) : (
            <div className="w-full h-full flex flex-col items-center justify-center">
              <Lock className="h-12 w-12 text-slate-500 mb-2" />
              <p className="text-slate-500 text-sm">Locked</p>
            </div>
          )}
          
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-transparent to-transparent" />
          
          {villain.completed && (
            <div className="absolute top-2 right-2 bg-green-500 rounded-full p-1.5">
              <CheckCircle className="h-4 w-4 text-white" />
            </div>
          )}
          
          {!villain.unlocked && isAdmin && !isTeaser && (
            <div className="absolute top-2 left-2 bg-red-600/80 rounded px-2 py-1 flex items-center gap-1">
              <Eye className="h-3 w-3 text-white" />
              <span className="text-xs text-white">Preview</span>
            </div>
          )}
          
          {isTeaser && (
            <div className="absolute top-2 left-2 bg-purple-600/80 rounded px-2 py-1 flex items-center gap-1">
              <Sparkles className="h-3 w-3 text-white" />
              <span className="text-xs text-white">Sneak Peek!</span>
            </div>
          )}
        </div>
        
        {/* Card Footer */}
        <div className={cn(
          "p-2 border-t",
          showContent ? `${tierStyle.border} border-opacity-30 bg-slate-900/90` : "border-slate-700 bg-slate-900"
        )}>
          {showContent ? (
            <>
              <p className={cn("font-semibold text-sm truncate", tierStyle.text)}>{villain.name}</p>
              <div className="flex items-center justify-between mt-1">
                <div className="flex items-center gap-1">
                  <Star className="h-3 w-3 text-cyan-400" />
                  <span className="text-cyan-400 text-xs">{tier * 50} pts</span>
                </div>
                <div className="flex items-center gap-1">
                  <Coins className="h-3 w-3 text-yellow-400" />
                  <span className="text-yellow-400 text-xs font-bold">{coinCost}</span>
                </div>
              </div>
            </>
          ) : (
            <p className="text-slate-500 text-sm">???</p>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default SquidTrench;

// Also export an embedded version without Navigation/Footer
export { SquidTrench };
